import React, { memo, useCallback } from 'react'
import { Link } from 'react-router-dom'
import moment from 'moment'
import InfoIcon from 'transfix-icons/build/InfoIcon'
import OpenInNewTabIcon from 'transfix-icons/build/OpenInNewTabIcon'
import RemoveIcon from 'transfix-icons/build/RemoveIcon'
import { Button } from 'transfix-ui/components/Button'
import { TableCell, TableRow } from 'transfix-ui/components/Table'
import { Tooltip } from 'transfix-ui/components/Tooltip'
import { Link as MuiLink, Box } from 'transfix-ui/core'
import { makeStyles, createStyles } from 'transfix-ui/core/styles'
import { brandColors } from 'transfix-ui/theme/colors'
import {
  IMotorCarrier,
  IUserMotorCarrier,
  MotorCarrierState,
  IMotorCarrierApplication,
} from 'apps/fleet-planner/types'
import { FORMATS } from 'constants/datetime'

const EM_SPAN = <span>&nbsp;&mdash;&nbsp;</span>

interface ICarriersListRowProps {
  userMotorCarrier: IUserMotorCarrier
  onRemoveCarrier: (motorCarrier: IMotorCarrier) => void
}

const valueOrEmdash = (value: number | string) => {
  return value ? <span>{value}</span> : EM_SPAN
}

const getStateText = (state?: MotorCarrierState) => {
  switch (state) {
    // Grouped as "Not Active" ...
    case 'cannot-use':
    case 'conditional':
    case 'do_not_use':
    case 'inactive':
    case 'other':
    case 'out_of_service':
    case 'probation':
      return 'Not Active'
    // Using the same messaging as Stark for the remainder of states ...
    case 'activated':
      return 'Activated'
    case 'application_submitted':
      return 'Application Submitted'
    case 'expired_insurance':
      return 'Expired Insurance'
    case 'fresh':
      return 'Fresh'
    case 'in_review':
      return 'In Review'
    case 'new':
      return 'New'
    case 'pending':
      return 'Pending'
    // E.g. if there isn't an associated carrier application
    default:
      return ''
  }
}

export const formatState = (application?: IMotorCarrierApplication) => {
  const { state } = application || {}
  const stateText = getStateText(state)
  return stateText
}
interface IGetShowFinishSetupLinkProps {
  state: MotorCarrierState | undefined | ''
  hasSignedBca: boolean | undefined
  hasUploadedDocuments: boolean | undefined
}
const getShowFinishSetupLink = ({
  hasSignedBca,
  hasUploadedDocuments,
  state = '',
}: IGetShowFinishSetupLinkProps) => {
  const semicompleteStates = ['application_submitted', 'pending']
  if (state === 'expired_insurance') {
    return true
  }
  if (
    ['in_review', ...semicompleteStates].includes(state || '') &&
    hasSignedBca &&
    hasUploadedDocuments
  ) {
    return false
  }
  if (semicompleteStates.includes(state || '')) {
    return true
  }
  return false
}

const FormatState = ({ application }: { application?: IMotorCarrierApplication }) => {
  const { hasSignedBca, hasUploadedDocuments, id: applicationId, state } = application || {}

  // TODO append "Finish Setup" link and track CARRIERS_FINISH_SETUP_CLICKED after FP-2141
  const stateText = formatState(application)

  const activateHandler = () => {
    window.open(`${__ROOT__}/fms/activate/${applicationId}`)
  }

  const showCarrierApplicationStateButton = getShowFinishSetupLink({
    hasSignedBca,
    hasUploadedDocuments,
    state,
  })

  return showCarrierApplicationStateButton ? (
    <Box data-test='carrier-application-state'>
      {stateText}
      <Box display='inline-flex' alignItems='center' ml='5px'>
        <MuiLink data-test='finish-setup-link' onClick={activateHandler}>
          Finish Setup
          <OpenInNewTabIcon />
        </MuiLink>
      </Box>
    </Box>
  ) : (
    valueOrEmdash(stateText)
  )
}

interface ITeamMembersLinkProps {
  usersCount: number
}

const TeamMembersLink = memo<ITeamMembersLinkProps>(({ usersCount }) => {
  switch (usersCount) {
    case undefined:
      return EM_SPAN
    // we want to exclude ourselves from the user count
    case 1:
      return (
        <>
          <span>None Yet. </span>
          <Link to='team'>Add Team Members</Link>
        </>
      )
    case 2:
      return <Link to='team'>1 Team Member</Link>
    default:
      return <Link to='team'>{usersCount - 1} Team Members</Link>
  }
})

type TClassKey = 'carrierCell' | 'span'

interface IUseCarriersTableRowStylesProps {
  isPending: boolean
}

const useCarriersTableRowStyles = makeStyles(
  createStyles<TClassKey, IUseCarriersTableRowStylesProps>({
    carrierCell: {
      color: props => (props.isPending ? brandColors.coolGray5 : ''),
    },
    span: {
      verticalAlign: 'middle !important',
      padding: '6px !important',
    },
  })
)

export const CarriersListRow = memo<ICarriersListRowProps>(
  ({ onRemoveCarrier, userMotorCarrier }) => {
    const { carrierApplication, membership, motorCarrier } = userMotorCarrier || {}

    const classes = useCarriersTableRowStyles({ isPending: !membership })

    const removeCarrierHandler = useCallback(() => onRemoveCarrier(motorCarrier), [
      onRemoveCarrier,
      motorCarrier,
    ])

    return (
      <TableRow>
        <TableCell className={classes.carrierCell}>
          {motorCarrier.name}
          {!membership && (
            <Tooltip
              position='right'
              type='dark'
              trigger={
                <span className={classes.span}>
                  <InfoIcon color='coolGray5' />
                </span>
              }>
              {' '}
              You requested approval to manage this carrier. An email was sent to the carrier’s
              Admin to approve you.
            </Tooltip>
          )}
        </TableCell>
        <TableCell>{valueOrEmdash(motorCarrier.dotNumber)}</TableCell>
        <TableCell>{valueOrEmdash(motorCarrier.mcNumber)}</TableCell>
        <TableCell>
          <TeamMembersLink usersCount={membership?.usersCount} />
        </TableCell>
        <TableCell>
          <FormatState application={carrierApplication} />
        </TableCell>
        <TableCell>{moment(membership?.createdAt).format(FORMATS.readableFormat)}</TableCell>
        <TableCell>
          {!!carrierApplication?.state && (
            <Button color='tertiary' onClick={removeCarrierHandler} startIcon={<RemoveIcon />}>
              Remove Carrier
            </Button>
          )}
        </TableCell>
      </TableRow>
    )
  }
)
